import type { DateChangeField } from '@/types';

/**
 * Human-readable label for a date field.
 *
 * Typed as an exhaustive `Record<DateChangeField, string>` on purpose: widening
 * `DateChangeField` without adding a label here is a compile error, not a dashboard row that
 * silently falls back to the raw snake_case column name.
 */
export const DATE_FIELD_LABELS: Record<DateChangeField, string> = {
  planned_kickoff_date: 'Planned Kickoff',
  expected_completion_date: 'Expected Completion',
  planned_ipr_date: 'Planned IPR',
};

export function dateFieldLabel(field: string): string {
  return DATE_FIELD_LABELS[field as DateChangeField] ?? field;
}

/** Format an ISO/YYYY-MM-DD date for display, or an em dash when null. */
export function formatDateValue(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/**
 * Convert an ISO timestamp or date to the `YYYY-MM-DD` value a native
 * `<input type="date">` expects. Returns '' when the input is null/invalid.
 */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/** Compact "x ago" from an ISO timestamp. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
